/**
 * Переключатель языка в шапке.
 *
 * У вошедшего пользователя выбор сохраняется в профиле: язык нужен серверу,
 * чтобы письма приходили на понятном человеку языке, а не на языке того
 * браузера, из которого он когда-то регистрировался.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateLanguage } from "../api/queries";
import { LANGUAGES, LANGUAGE_NAMES, currentLanguage, setLanguage, type Language } from "../i18n";
import { useAuthStore } from "../stores/authStore";

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const authenticated = useAuthStore((state) => state.status) === "authenticated";
  const remember = useUpdateLanguage();

  // Клик мимо меню закрывает его: иначе список висит поверх страницы.
  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choose = (language: Language) => {
    setLanguage(language);
    setOpen(false);
    // Отказ сервера не должен мешать смене языка на экране: она уже произошла.
    if (authenticated) remember.mutate(language);
  };

  const active = currentLanguage();

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        className="btn-ghost px-2.5"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language.switch")}
        title={t("language.switch")}
      >
        {/* Без иконки: глобус рядом с кодом языка ничего не добавлял, а место
            в шапке занимал — там и так тесно от комнаты, обратной связи и
            профиля. Код языка сам по себе понятен. */}
        <span className="text-xs font-medium uppercase">{active}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full z-40 mt-1 min-w-[9rem] overflow-hidden rounded-xl
                     border border-ink-700 bg-ink-900 py-1 shadow-panel"
        >
          {LANGUAGES.map((language) => (
            <li key={language}>
              <button
                type="button"
                role="option"
                aria-selected={language === active}
                className={`flex w-full items-center justify-between px-3 py-2 text-sm transition-colors
                  ${language === active ? "text-amber-300" : "text-ink-200 hover:bg-ink-850"}`}
                onClick={() => choose(language)}
              >
                {LANGUAGE_NAMES[language]}
                {language === active && <CheckIcon />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path d="M4 12.5l5 5L20 6.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
